import type { featuredProperties } from '../../../mocks/properties';

type Property = (typeof featuredProperties)[number];

interface Props {
  property: Property;
  isEditorsPick?: boolean;
}

export default function CabinCard({ property, isEditorsPick }: Props) {
  return (
    <article className={`bg-white rounded-2xl overflow-hidden flex flex-col lg:flex-row gap-0 relative ${isEditorsPick ? 'ring-2 ring-[#2D4A3E]' : ''}`}>
      {isEditorsPick && (
        <div className="absolute top-4 right-4 z-20 flex items-center gap-1.5 bg-[#2D4A3E] text-white text-xs tracking-[0.15em] uppercase font-jost px-3 py-1.5 rounded-full">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-award-line text-[#C9A96E] text-xs" />
          </div>
          Editor&apos;s Pick
        </div>
      )}
      {/* Image — clickable */}
      <a
        href={property.airbnbUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="lg:w-[42%] h-64 lg:h-auto overflow-hidden shrink-0 block group cursor-pointer"
      >
        <img
          src={property.image}
          alt={`${property.name} - cabin in Highlands NC`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </a>

      {/* Content */}
      <div className="flex-1 p-8 flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <span className="text-[#6B8C7D] text-xs tracking-widest uppercase font-jost block mb-1">{property.type}</span>
              <a
                href={property.airbnbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer hover:text-[#2D4A3E] transition-colors"
              >
                <h3 className="font-cormorant text-3xl text-[#1A1A18] font-medium leading-tight">{property.name}</h3>
              </a>
              <p className="text-[#9C9890] text-sm font-jost mt-1">{property.location}</p>
            </div>
          </div>

          <p className="text-[#5C5850] text-sm font-jost leading-relaxed mb-5">{property.description}</p>

          <div className="flex flex-wrap gap-2 mb-5">
            {property.features.slice(0, 4).map((feat) => (
              <span
                key={feat}
                className="bg-[#F5F1EB] text-[#5C5850] text-xs font-jost px-3 py-1.5 rounded-full"
              >
                {feat}
              </span>
            ))}
            {property.features.length > 4 && (
              <span className="bg-[#F5F1EB] text-[#9C9890] text-xs font-jost px-3 py-1.5 rounded-full">
                +{property.features.length - 4} more
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-5 border-t border-[#F5F1EB]">
          <div className="flex items-center gap-3">
            {property.petFriendly && (
              <span className="flex items-center gap-1.5 text-[#6B8C7D] text-xs font-jost bg-[#EEF5EE] px-3 py-1.5 rounded-full">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-paw-print-line text-xs" />
                </div>
                Pet Friendly
              </span>
            )}
            {property.nearWaterfall && (
              <span className="flex items-center gap-1.5 text-[#3A6A8C] text-xs font-jost bg-[#EEF2F8] px-3 py-1.5 rounded-full">
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-water-flash-line text-xs" />
                </div>
                Near Falls
              </span>
            )}
          </div>
          <a
            href={property.airbnbUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-[#2D4A3E] text-white text-xs font-jost tracking-[0.12em] uppercase px-5 py-2.5 rounded-full cursor-pointer hover:bg-[#1e3329] transition-colors duration-200"
          >
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-external-link-line text-xs" />
            </div>
            View on Airbnb
          </a>
        </div>
      </div>
    </article>
  );
}
